import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { memberApi, useMemberAuth } from "@/lib/memberAuth";
import { Users, CalendarDots, Handshake, ArrowRight, Globe } from "@phosphor-icons/react";

const Card = ({ to, icon: Icon, title, description, count, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group bg-white border border-black/10 p-6 flex flex-col hover:border-navy transition-colors"
  >
    <div className="flex items-center justify-between">
      <Icon size={22} weight="duotone" className="text-ember" />
      {count != null && <span className="ship-number text-2xl font-bold text-navy" data-testid={`${testid}-count`}>{count}</span>}
    </div>
    <div className="font-display font-bold text-lg text-navy mt-4">{title}</div>
    <p className="text-sm text-navy/70 mt-1 flex-1">{description}</p>
    <div className="text-xs uppercase tracking-widest text-navy/60 group-hover:text-ember mt-4 flex items-center gap-1">
      Open <ArrowRight size={12} />
    </div>
  </Link>
);

export default function MemberDashboard() {
  const { member } = useMemberAuth();
  const [dirCount, setDirCount] = useState(null);
  const [eventCount, setEventCount] = useState(null);
  const [matchCount, setMatchCount] = useState(null);

  useEffect(() => {
    memberApi.get("/member/directory").then(({ data }) => setDirCount((data.members || []).length)).catch(() => {});
    memberApi.get("/member/events").then(({ data }) => setEventCount((data.events || []).length)).catch(() => {});
    memberApi.get("/member/matches").then(({ data }) => setMatchCount((data.matches || []).length)).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-dashboard">
      <div className="mb-8">
        <div className="overline mb-3">Portal · Signed in</div>
        <h1 className="font-display text-4xl font-bold text-navy tracking-tight">
          Welcome, {member.company_name}
        </h1>
        <p className="text-navy/70 mt-2 flex items-center gap-1.5 text-sm">
          <Globe size={14} /> {member.city ? `${member.city}, ` : ""}{member.country} · {member.membership_type?.replace("_", " ") || "member"}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card
          to="/portal/directory"
          icon={Users}
          title="Member directory"
          description="Search the private member list. Countries, corridors, services, contacts."
          count={dirCount}
          testid="dashboard-directory-card"
        />
        <Card
          to="/portal/events"
          icon={CalendarDots}
          title="Events"
          description="Upcoming EPAS meetings — RSVP, capacity, location. One-click yes / no / maybe."
          count={eventCount}
          testid="dashboard-events-card"
        />
        <Card
          to="/portal/matches"
          icon={Handshake}
          title="Introductions"
          description="Suggested partner introductions based on shared corridors and services."
          count={matchCount}
          testid="dashboard-matches-card"
        />
      </div>

      <div className="mt-10 border border-black/10 bg-white p-6" data-testid="dashboard-network-brief">
        <div className="overline mb-2">Network brief</div>
        <p className="text-navy/80 text-sm leading-relaxed">
          Reminder: everything in this portal is private to EPAS members. The public site shows aggregate stats
          only — never your contact details, your trade-lane notes or another member&apos;s contact person. If you
          spot a competitor publishing member PII, forward it to{" "}
          <a href="mailto:rok@epas.one" className="text-ember hover:underline">rok@epas.one</a> — we log the pattern
          into the playbook so our own agents never repeat it.
        </p>
      </div>
    </div>
  );
}
